import { UserAvatarBadge } from 'app/components/user-avatar-badge';
import { ClubName } from 'app/components/club-name';
import { Column, Row } from 'app/design/primitives/box';
import { PCImage } from 'app/design/primitives/image';
import { Text } from 'app/design/primitives/text';
import { useNavigation } from 'app/hooks/navigation';
import { getImageVariantUrl } from 'app/lib/image-variants';
import React from 'react';
import { Pressable, View } from 'react-native';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';

import { localeAwareFormatDistance } from 'app/lib/date';
import { useTranslation } from 'react-i18next';
import type { PartialListingGrouping } from 'app/types/listing';
import { navigateToListing } from 'app/screens/billboard/navigator-helper';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconChevronRight } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
const theme = createDSTheme();

function MemberDistance({
  city,
  distance,
  formatDistance,
}: {
  city?: string;
  distance?: number;
  formatDistance: (distance: number) => string;
}) {
  if (distance === undefined) return null;

  return (
    <Row style={{ marginRight: 40 }}>
      {city ? (
        <Text numberOfLines={1} variant="bodyBase">
          {`${city} - `}
        </Text>
      ) : null}
      <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
        {formatDistance(distance)}
      </Text>
    </Row>
  );
}

function MemberActivity({
  lastSeenAt,
  trustPoints,
}: {
  lastSeenAt?: string | Date | null;
  trustPoints?: number | null;
}) {
  const { t } = useTranslation();
  if (!trustPoints && !lastSeenAt) return null;

  return (
    <Text color="text.tertiary" numberOfLines={1} style={{ width: '100%' }} variant="bodyBase">
      {trustPoints ? `👍 (${trustPoints.toFixed(0)})` : ''}
      {trustPoints && lastSeenAt ? ' | ' : ''}
      {lastSeenAt
        ? t('lastActiveAgo', {
            ago: localeAwareFormatDistance(new Date(), new Date(lastSeenAt)),
            ns: 'common',
          })
        : ''}
    </Text>
  );
}

export default function MemberRow(listing: PartialListingGrouping['listings'][number] & { city?: string }) {
  const { formatDistanceWithoutText } = useDistanceUnit();
  const navigation = useNavigation<'grouped-listing'>();

  const user = listing.user;

  function goToListing() {
    return navigateToListing(navigation, listing.id);
  }

  return (
    <Pressable
      onPress={goToListing}
      style={{ borderBottomWidth: 1, borderBottomColor: theme.colors['border.secondary'] }}
    >
      <Row alignItems="center" gap="spacing.general.x4" paddingVertical="spacing.general.x5">
        <Row>
          <PCImage
            aspectRatio={1}
            cachePolicy="memory-disk"
            height={80}
            recyclingKey={`${user.profile.picture!}-member-card`}
            source={{ uri: getImageVariantUrl(user.profile.picture, 'medium') }}
            style={{ borderRadius: 40 }}
          />
          <UserAvatarBadge
            userId={user.id}
            avatarSize={80}
            size={50}
            isAmbassador={user.isAmbassador}
            isSuper={user.isSuper}
          />
        </Row>

        <Column flex={1} gap="spacing.general.x0,5" justifyContent="space-around">
          <Text fontWeight="700" numberOfLines={1} variant="bodyBase">
            {`${user.profile.firstName} ${user.profile.lastName}`}
          </Text>

          <MemberDistance city={listing.city} distance={listing.distance} formatDistance={formatDistanceWithoutText} />

          <MemberActivity lastSeenAt={user.lastSeenAt} trustPoints={user.trustPoints} />

          {listing.groups ? (
            <Column>
              <ClubName asTags groups={listing.groups} preventNavigateToClubMembers />
            </Column>
          ) : null}
        </Column>

        <View style={{ marginLeft: -theme.spacing['spacing.general.x2'] }}>
          <IconChevronRight strokeWidth={ICON_STROKE_WIDTH} size={30} />
        </View>
      </Row>
    </Pressable>
  );
}
